import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FiVolume2, FiCheck, FiX } from 'react-icons/fi';
import FlashcardsNav from './FlashcardsNav';
import { generateTTS as generateTTSCloudFunction } from '../chat/openAIUtilsCloudFunctions';
import './theme.css';
import './card.css';

// Question types
const QUESTION_TYPES = {
  MULTIPLE_CHOICE: 'multiple_choice',
  FILL_BLANK: 'fill_blank',
  TRUE_FALSE: 'true_false',
  AUDIO_RECALL: 'audio_recall'
};

// Spaced repetition intervals (in days) - SuperMemo 2 algorithm inspired
const SPACED_INTERVALS = [1, 3, 7, 14, 30, 90, 180, 365];

// Difficulty levels for spaced repetition
const DIFFICULTY_LEVELS = {
  AGAIN: 0,      // Complete failure to recall
  HARD: 1,       // Correct response recalled with serious difficulty
  GOOD: 2,       // Correct response after some hesitation
  EASY: 3        // Perfect response with no hesitation
};

const StudyMode = () => {
  const { deckId } = useParams();
  const [user] = useAuthState(auth);
  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Study session state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionType, setQuestionType] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [sessionProgress, setSessionProgress] = useState({ correct: 0, total: 0 });
  const [sessionCards, setSessionCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  
  // Audio state
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Reset audio state when question changes
  useEffect(() => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // Reset audio state
    setIsPlaying(false);
    setAudioUrl(null);
  }, [currentQuestion?.audioText]); // Reset when audio text changes

  // Cleanup audio URL on unmount or when audioUrl changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    if (deckId) {
      fetchDeck();
    }
  }, [deckId]);

  const fetchDeck = async () => {
    if (!deckId) {
      setError('Deck ID is not defined');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching deck with ID:', deckId);
      const ref = doc(db, 'flashcardLibrary', deckId);
      const snap = await getDoc(ref);
      
      if (!snap.exists()) {
        setError('Deck not found');
        setLoading(false);
        return;
      }
      
      const data = snap.data();
      console.log('Raw Firebase data:', data);
      
      if (!data.deck) {
        setError('Invalid deck data');
        setLoading(false);
        return;
      }
      
      const parsed = JSON.parse(data.deck);
      console.log('Parsed deck:', parsed);
      
      if (!parsed.cards || !Array.isArray(parsed.cards) || parsed.cards.length === 0) {
        setError('No cards found in deck');
        setLoading(false);
        return;
      }
      
      setDeck(parsed);
      
      // Load user progress
      if (user) {
        await loadUserProgress(parsed.cards);
      }
      
      // Initialize study session
      initializeStudySession(parsed.cards);
    } catch (e) {
      console.error('Error fetching deck:', e);
      setError('Failed to load deck: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async (cards) => {
    if (!user) return;
    
    try {
      const progressRef = doc(db, 'users', user.uid, 'studyProgress', deckId);
      const progressSnap = await getDoc(progressRef);
      if (progressSnap.exists()) {
        const progress = progressSnap.data();
        // Merge progress with cards
        cards.forEach(card => {
          const cardProgress = progress[card.term] || {
            level: 0,
            nextReview: new Date().toISOString(),
            correctCount: 0,
            incorrectCount: 0
          };
          card.progress = cardProgress;
        });
      }
    } catch (e) {
      console.error('Error loading progress:', e);
    }
  };

  const initializeStudySession = (cards) => {
    if (!cards || cards.length === 0) {
      setError('No cards available for study');
      return;
    }

    // Filter cards that need review (due for review or never studied)
    const now = new Date();
    const cardsToStudy = cards.filter(card => {
      const progress = card.progress || { level: 0, nextReview: new Date(0).toISOString() };
      return new Date(progress.nextReview) <= now;
    });

    // If no cards due for review, include some random cards
    if (cardsToStudy.length === 0) {
      const randomCards = cards.sort(() => 0.5 - Math.random()).slice(0, Math.min(10, cards.length));
      setSessionCards(randomCards);
    } else {
      setSessionCards(cardsToStudy);
    }

    // Generate first question
    const firstCard = cardsToStudy.length > 0 ? cardsToStudy[0] : cards[0];
    if (firstCard) {
      generateQuestion(firstCard);
    }
  };

  const generateQuestion = (card) => {
    if (!card || !deck) {
      console.error('Cannot generate question: card or deck is null');
      return;
    }

    // Filter out question types that don't work well with this card
    const availableTypes = [];
    
    // Multiple choice always works
    availableTypes.push(QUESTION_TYPES.MULTIPLE_CHOICE);
    
    // Fill blank only if we have explanation/translation with enough words
    const explanation = card.explanation || card.translation;
    if (explanation && explanation.split(' ').length > 3) {
      availableTypes.push(QUESTION_TYPES.FILL_BLANK);
    }
    
    // True/False only if we have both term and explanation
    if (card.term && explanation) {
      availableTypes.push(QUESTION_TYPES.TRUE_FALSE);
    }
    
    // Audio recall only if we have a term to speak
    if (card.term) {
      availableTypes.push(QUESTION_TYPES.AUDIO_RECALL);
    }
    
    if (availableTypes.length === 0) {
      // Fallback to multiple choice
      availableTypes.push(QUESTION_TYPES.MULTIPLE_CHOICE);
    }
    
    const randomType = availableTypes[Math.floor(Math.random() * availableTypes.length)];
    setQuestionType(randomType);
    
    let question = null;
    switch (randomType) {
      case QUESTION_TYPES.MULTIPLE_CHOICE:
        question = generateMultipleChoice(card);
        break;
      case QUESTION_TYPES.FILL_BLANK:
        question = generateFillBlank(card);
        break;
      case QUESTION_TYPES.TRUE_FALSE:
        question = generateTrueFalse(card);
        break;
      case QUESTION_TYPES.AUDIO_RECALL:
        question = generateAudioRecall(card);
        break;
      default:
        question = generateMultipleChoice(card);
    }
    
    if (question) {
      setCurrentQuestion(question);
      setUserAnswer('');
      setShowAnswer(false);
      setIsCorrect(null);
    }
  };

  const generateMultipleChoice = (card) => {
    if (!card || !deck || !deck.cards) {
      console.error('Cannot generate multiple choice: missing data');
      return null;
    }

    const correctAnswer = (card.translation || card.explanation) || card.term;
    const options = [correctAnswer];
    
    // Add wrong options from other cards
    const otherCards = deck.cards.filter(c => c !== card);
    const shuffled = otherCards.sort(() => 0.5 - Math.random());
    shuffled.slice(0, 3).forEach(c => {
      options.push((c.translation || c.explanation) || c.term);
    });
    
    return {
      type: QUESTION_TYPES.MULTIPLE_CHOICE,
      question: `What is the meaning of "${card.term}"?`,
      options: options.sort(() => 0.5 - Math.random()),
      correctAnswer,
      card
    };
  };

  const generateFillBlank = (card) => {
    const explanation = card.explanation || card.translation;
    if (!explanation) {
      return generateMultipleChoice(card); // Fallback to multiple choice
    }

    const words = explanation.split(' ');
    const randomIndex = Math.floor(Math.random() * words.length);
    const blankWord = words[randomIndex];
    words[randomIndex] = '_____';
    
    return {
      type: QUESTION_TYPES.FILL_BLANK,
      question: `Complete the sentence: ${words.join(' ')}`,
      correctAnswer: blankWord,
      card
    };
  };

  const generateTrueFalse = (card) => {
    const isTrue = Math.random() > 0.5;
    let statement, correctAnswer;
    
    const translation = card.translation || card.explanation;
    
    if (isTrue) {
      statement = `${card.term} means "${translation}"`;
      correctAnswer = true;
    } else {
      const wrongTranslation = translation + ' (incorrect)';
      statement = `${card.term} means "${wrongTranslation}"`;
      correctAnswer = false;
    }
    
    return {
      type: QUESTION_TYPES.TRUE_FALSE,
      question: statement,
      correctAnswer,
      card
    };
  };

  const generateAudioRecall = (card) => {
    return {
      type: QUESTION_TYPES.AUDIO_RECALL,
      question: 'Listen to the audio and identify the term:',
      correctAnswer: card.term,
      card,
      audioText: card.term
    };
  };

  const generateTTS = async (text, retryCount = 0) => {
    try {
      // Validate text before sending to API
      if (!text || text.trim().length === 0) {
        console.warn('TTS: Empty text provided');
        return null;
      }

      // Limit text length to prevent API errors
      const maxLength = 1000;
      const truncatedText = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

      console.log('TTS: Generating audio for text:', truncatedText.substring(0, 100) + '...');
      
      // Try different voices based on retry count
      const voices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
      const voiceIndex = retryCount % voices.length;
      const selectedVoice = voices[voiceIndex];
      
      console.log(`TTS: Attempt ${retryCount + 1}, using voice: ${selectedVoice}`);
      
      // Use Cloud Functions instead of direct API call
      const blob = await generateTTSCloudFunction(
        truncatedText,
        'Flashcard Generator',
        null,
        {
          model: 'tts-1',
          voice: selectedVoice,
        }
      );
      
      if (!blob) {
        throw new Error('TTS: No audio data received');
      }
      
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      console.log('TTS: Audio generated successfully');
      return url;
    } catch (e) {
      console.error('TTS Error:', e);
      
      // Retry with different voice if we haven't tried all voices yet
      const voices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
      if (retryCount < voices.length - 1 && (e.status === 500 || e.status === 429)) {
        console.log(`TTS: Retrying with different voice (attempt ${retryCount + 2})`);
        return generateTTS(text, retryCount + 1);
      }
      
      return null;
    }
  };

  const handleAudio = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      return;
    }

    if (!currentQuestion?.audioText) return;

    console.log('TTS: Speaking text for study mode:', currentQuestion.audioText.substring(0, 100) + '...');

    setIsPlaying(true);
    
    // Always generate fresh TTS for current question
    const url = await generateTTS(currentQuestion.audioText);

    if (url && audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.onerror = () => setIsPlaying(false);
      audioRef.current.play().catch(console.error);
    } else {
      setIsPlaying(false);
    }
  };

  const checkAnswer = (answer) => {
    if (!currentQuestion) return;

    let correct = false;
    switch (currentQuestion.type) {
      case QUESTION_TYPES.MULTIPLE_CHOICE:
        correct = answer === currentQuestion.correctAnswer;
        break;
      case QUESTION_TYPES.FILL_BLANK:
        correct = answer.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase().trim();
        break;
      case QUESTION_TYPES.TRUE_FALSE:
        correct = answer === currentQuestion.correctAnswer;
        break;
      case QUESTION_TYPES.AUDIO_RECALL:
        correct = answer.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase().trim();
        break;
    }

    setUserAnswer(answer);
    setIsCorrect(correct);
    setShowAnswer(true);
    
    // Update progress with default difficulty (will be updated when user rates difficulty)
    updateCardProgress(currentQuestion.card, correct, DIFFICULTY_LEVELS.GOOD);
    
    // Update session progress
    setSessionProgress(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const rateDifficulty = (difficulty) => {
    if (!currentQuestion) return;
    
    // Update the progress with the user's difficulty rating
    updateCardProgress(currentQuestion.card, isCorrect, difficulty);
  };

  const updateCardProgress = async (card, isCorrect, difficulty = null) => {
    if (!user) return;

    const progress = card.progress || {
      level: 0,
      nextReview: new Date().toISOString(),
      correctCount: 0,
      incorrectCount: 0,
      consecutiveCorrect: 0,
      easeFactor: 2.5, // Starting ease factor
      interval: 1
    };

    if (isCorrect) {
      progress.correctCount++;
      progress.consecutiveCorrect++;
      
      // Determine difficulty level if not provided
      if (difficulty === null) {
        difficulty = DIFFICULTY_LEVELS.GOOD; // Default to good
      }
      
      // Update ease factor based on difficulty
      if (difficulty === DIFFICULTY_LEVELS.AGAIN) {
        progress.easeFactor = Math.max(1.3, progress.easeFactor - 0.2);
        progress.consecutiveCorrect = 0;
        progress.level = Math.max(0, progress.level - 1);
      } else if (difficulty === DIFFICULTY_LEVELS.HARD) {
        progress.easeFactor = Math.max(1.3, progress.easeFactor - 0.15);
        progress.level = Math.max(0, progress.level - 0.5);
      } else if (difficulty === DIFFICULTY_LEVELS.GOOD) {
        progress.easeFactor = Math.max(1.3, progress.easeFactor + 0.1);
        progress.level = Math.min(progress.level + 1, SPACED_INTERVALS.length - 1);
      } else if (difficulty === DIFFICULTY_LEVELS.EASY) {
        progress.easeFactor = Math.max(1.3, progress.easeFactor + 0.15);
        progress.level = Math.min(progress.level + 1.5, SPACED_INTERVALS.length - 1);
      }
      
      // Calculate interval using ease factor
      if (progress.level === 0) {
        progress.interval = 1;
      } else if (progress.level === 1) {
        progress.interval = 3;
      } else {
        progress.interval = Math.round(progress.interval * progress.easeFactor);
      }
    } else {
      progress.incorrectCount++;
      progress.consecutiveCorrect = 0;
      progress.easeFactor = Math.max(1.3, progress.easeFactor - 0.2);
      progress.level = Math.max(0, progress.level - 1);
      progress.interval = 1;
    }

    // Calculate next review date
    const nextReview = new Date();
    nextReview.setDate(nextReview.getDate() + progress.interval);
    progress.nextReview = nextReview.toISOString();

    // Save to Firebase
    try {
      const progressRef = doc(db, 'users', user.uid, 'studyProgress', deckId);
      await setDoc(progressRef, {
        [card.term]: progress
      }, { merge: true });
    } catch (e) {
      console.error('Error saving progress:', e);
    }
  };

  const nextQuestion = () => {
    const nextIndex = currentCardIndex + 1;
    if (nextIndex < sessionCards.length) {
      setCurrentCardIndex(nextIndex);
      generateQuestion(sessionCards[nextIndex]);
    } else {
      // Session complete
      setCurrentQuestion(null);
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case QUESTION_TYPES.MULTIPLE_CHOICE:
        return (
          <div className="question-container">
            <h3 className="question-title">{currentQuestion.question}</h3>
            <div className="options-grid">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  className={`option-button ${userAnswer === option ? 'selected' : ''} ${showAnswer ? (option === currentQuestion.correctAnswer ? 'correct' : userAnswer === option ? 'incorrect' : '') : ''}`}
                  onClick={() => !showAnswer && checkAnswer(option)}
                  disabled={showAnswer}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        );

      case QUESTION_TYPES.FILL_BLANK:
        return (
          <div className="question-container">
            <h3 className="question-title">{currentQuestion.question}</h3>
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !showAnswer && userAnswer.trim()) {
                  checkAnswer(userAnswer);
                }
              }}
              placeholder="Type your answer and press Enter..."
              className="fill-blank-input"
              disabled={showAnswer}
            />
          </div>
        );

      case QUESTION_TYPES.TRUE_FALSE:
        return (
          <div className="question-container">
            <h3 className="question-title">{currentQuestion.question}</h3>
            <div className="options-grid">
              <button
                className={`option-button ${userAnswer === true ? 'selected' : ''} ${showAnswer ? (true === currentQuestion.correctAnswer ? 'correct' : userAnswer === true ? 'incorrect' : '') : ''}`}
                onClick={() => !showAnswer && checkAnswer(true)}
                disabled={showAnswer}
              >
                True
              </button>
              <button
                className={`option-button ${userAnswer === false ? 'selected' : ''} ${showAnswer ? (false === currentQuestion.correctAnswer ? 'correct' : userAnswer === false ? 'incorrect' : '') : ''}`}
                onClick={() => !showAnswer && checkAnswer(false)}
                disabled={showAnswer}
              >
                False
              </button>
            </div>
          </div>
        );

      case QUESTION_TYPES.AUDIO_RECALL:
        return (
          <div className="question-container">
            <h3 className="question-title">{currentQuestion.question}</h3>
            <div className="audio-section">
              <button
                className={`audio-button ${isPlaying ? 'playing' : ''}`}
                onClick={handleAudio}
              >
                <FiVolume2 size={24} />
              </button>
            </div>
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !showAnswer && userAnswer.trim()) {
                  checkAnswer(userAnswer);
                }
              }}
              placeholder="Type what you heard and press Enter..."
              className="fill-blank-input"
              disabled={showAnswer}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-foreground">Loading study mode...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
        <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <FlashcardsNav />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <div className="card" style={{ padding: 24, maxWidth: 600, textAlign: 'center' }}>
              <h2 style={{ color: 'hsl(var(--destructive))', marginBottom: 16 }}>Error Loading Study Mode</h2>
              <p style={{ marginBottom: 24 }}>{error}</p>
              <button 
                className="nav-button primary" 
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
        <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <FlashcardsNav />
          <div className="study-complete-card">
            <h2 className="study-complete-title">
              Study Session Complete!
            </h2>
            <div className="study-complete-stats">
              <p className="study-complete-summary">
                You answered {sessionProgress.correct} out of {sessionProgress.total} questions correctly.
              </p>
              <p className="study-complete-accuracy">
                Accuracy: {sessionProgress.total > 0 ? Math.round((sessionProgress.correct / sessionProgress.total) * 100) : 0}%
              </p>
            </div>
            <button 
              className="study-complete-button"
              onClick={() => {
                setCurrentCardIndex(0);
                setSessionProgress({ correct: 0, total: 0 });
                if (deck && deck.cards) {
                  initializeStudySession(deck.cards);
                }
              }}
            >
              Start New Session
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
      <audio ref={audioRef} style={{ display: 'none' }} />
      <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <FlashcardsNav />
        
        <div className="card" style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
          {/* Progress Header */}
          <div className="study-header">
            <div className="progress-info">
              <span className="progress-current">{currentCardIndex + 1}</span>
              <span className="progress-separator">/</span>
              <span className="progress-total">{sessionCards.length}</span>
            </div>
            <div className="session-stats">
              <span className="stat-correct">✓ {sessionProgress.correct}</span>
              <span className="stat-incorrect">✗ {sessionProgress.total - sessionProgress.correct}</span>
            </div>
          </div>

          {/* Question */}
          <div className="question-section">
            {renderQuestion()}
          </div>

          {/* Answer Feedback */}
          {showAnswer && (
            <div className={`answer-feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
              <div className="feedback-icon">
                {isCorrect ? <FiCheck size={24} /> : <FiX size={24} />}
              </div>
              <div className="feedback-content">
                <h4>{isCorrect ? 'Correct!' : 'Incorrect'}</h4>
                <p>Answer: {currentQuestion.correctAnswer}</p>
                {(currentQuestion.card.explanation || currentQuestion.card.translation) && (
                  <p className="explanation">{currentQuestion.card.explanation || currentQuestion.card.translation}</p>
                )}
                
                {/* Difficulty Rating */}
                {isCorrect && (
                  <div className="difficulty-rating">
                    <p style={{ marginBottom: 8, fontSize: 14, color: 'hsl(var(--muted-foreground))' }}>
                      How difficult was this question?
                    </p>
                    <div className="difficulty-buttons">
                      <button 
                        className="difficulty-btn again"
                        onClick={() => rateDifficulty(DIFFICULTY_LEVELS.AGAIN)}
                      >
                        Again
                      </button>
                      <button 
                        className="difficulty-btn hard"
                        onClick={() => rateDifficulty(DIFFICULTY_LEVELS.HARD)}
                      >
                        Hard
                      </button>
                      <button 
                        className="difficulty-btn good"
                        onClick={() => rateDifficulty(DIFFICULTY_LEVELS.GOOD)}
                      >
                        Good
                      </button>
                      <button 
                        className="difficulty-btn easy"
                        onClick={() => rateDifficulty(DIFFICULTY_LEVELS.EASY)}
                      >
                        Easy
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Button */}
          {showAnswer && (
            <div className="study-actions">
              <button 
                className="study-complete-button"
                onClick={nextQuestion}
              >
                Next Question
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StudyMode;
